import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SEO from "@/components/SEO";
import Navbar, { MobileBottomBar, BookButton } from "@/components/Navbar";
import { Footer } from "@/components/Contact";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { api, imgSrc } from "@/lib/api";
import { useContent } from "@/App";

function renderContent(text) {
  return (text || "").split(/\n\n+/).map((block, i) => {
    if (block.startsWith("## ")) return <h2 key={i}>{block.slice(3)}</h2>;
    return <p key={i}>{block}</p>;
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const content = useContent();
  const [post, setPost] = useState(undefined);
  useEffect(() => { api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setPost(null)); }, [slug]);

  if (post === null) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-4" data-testid="blog-post-not-found">
        <p className="font-serif text-2xl text-cream">This guide has drifted out to sea.</p>
        <Link to="/blog" className="text-sm text-copper-300 hover:underline" data-testid="back-to-journal-link">Back to the Journal</Link>
      </main>
    );
  }
  if (!post) return <main className="min-h-screen" data-testid="blog-post-loading" />;

  const jsonLd = [
    { "@context": "https://schema.org", "@type": "BlogPosting", headline: post.title, description: post.excerpt, image: post.cover_image, datePublished: post.created_at },
    { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: window.location.origin },
      { "@type": "ListItem", position: 2, name: "Journal", item: `${window.location.origin}/blog` },
      { "@type": "ListItem", position: 3, name: post.title },
    ]},
  ];

  return (
    <main className="min-h-screen" data-testid="blog-post-page">
      <SEO title={`${post.title} — Mystique Copper Villa`} description={post.excerpt} image={post.cover_image} path={`/blog/${post.slug}`} jsonLd={jsonLd} />
      <Navbar />
      <article className="mx-auto max-w-3xl px-4 pb-20 pt-32 sm:px-8 lg:pt-40">
        <Link to="/blog" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-copper-300 hover:text-copper-400" data-testid="back-to-journal-link">
          <ArrowLeft className="h-3.5 w-3.5" /> Journal
        </Link>
        <h1 className="mt-6 font-serif text-4xl leading-[1.12] tracking-tight text-cream sm:text-5xl">
          <MaskedLine delay={0.2}>{post.title}</MaskedLine>
        </h1>
        {post.cover_image && (
          <Reveal delay={0.2} className="mt-10">
            <img src={imgSrc(post.cover_image)} alt={post.title} className="aspect-[16/9] w-full rounded-2xl border border-white/10 object-cover" />
          </Reveal>
        )}
        <Reveal delay={0.3}>
          <div className="prose-copper mt-10" data-testid="blog-post-content">{renderContent(post.content)}</div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-14 rounded-2xl border border-copper/30 bg-copper/10 p-8 text-center" data-testid="blog-post-cta">
            <p className="font-serif text-2xl text-cream">Turn this guide into a trip.</p>
            <p className="mt-2 text-sm font-light text-cream-muted">Book the entire villa direct and wake up inside the story.</p>
            <div className="mt-6 flex justify-center"><BookButton testid="blog-post-book-btn" /></div>
          </div>
        </Reveal>
      </article>
      <Footer settings={content?.settings} />
      <MobileBottomBar />
    </main>
  );
}
